import React, { useState, useEffect, useRef } from 'react';
import { movieApi, Movie, handleApiError } from '../services/api';

export type SearchType = 'movie' | 'actor' | 'director' | 'genre';

interface MovieSearchProps {
  onMovieSelect?: (movie: Movie) => void;
  onSearch?: (query: string, type: SearchType) => void;
  placeholder?: string;
  showResults?: boolean;
  className?: string;
}

const MovieSearch: React.FC<MovieSearchProps> = ({
  onMovieSelect,
  onSearch,
  placeholder,
  showResults = true,
  className = ""
}) => {
  const [query, setQuery] = useState('');
  const [searchType, setSearchType] = useState<SearchType>('movie');
  const [movies, setMovies] = useState<Movie[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  const getPlaceholder = () => {
    if (placeholder) return placeholder;
    switch (searchType) {
      case 'movie':
        return 'Search for a movie to get recommendations...';
      case 'actor':
        return 'Search by actor to get recommendations...';
      case 'director':
        return 'Search by director to get recommendations...';
      case 'genre':
        return 'Search by genre to get recommendations...';
      default:
        return 'Search...';
    }
  };

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (query.trim().length >= 2) {
        performSearch();
      } else {
        setMovies([]);
        setSuggestions([]);
        setShowDropdown(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, searchType]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const performSearch = async () => {
    if (!query.trim()) return;

    setIsLoading(true);
    setError('');

    try {
      if (searchType === 'movie') {
        const response = await movieApi.searchMovies(query, 8);
        setMovies(response.movies);
        setSuggestions([]);
        setShowDropdown(showResults && response.movies.length > 0);
      } else {
        // For non-movie searches, get suggestions for autocomplete
        let suggestionList: string[] = [];
        if (searchType === 'actor') {
          const response = await movieApi.getActors();
          suggestionList = response.actors
            .filter(actor => actor.name.toLowerCase().includes(query.toLowerCase()))
            .slice(0, 8)
            .map(actor => actor.name);
        } else if (searchType === 'director') {
          const response = await movieApi.getDirectors();
          suggestionList = response.directors
            .filter(director => director.name.toLowerCase().includes(query.toLowerCase()))
            .slice(0, 8)
            .map(director => director.name);
        } else if (searchType === 'genre') {
          const response = await movieApi.getGenres();
          suggestionList = response.genres
            .filter(genre => genre.name.toLowerCase().includes(query.toLowerCase()))
            .slice(0, 8)
            .map(genre => genre.name);
        }
        setSuggestions(suggestionList);
        setMovies([]);
        setShowDropdown(showResults && suggestionList.length > 0);
      }
    } catch (err) {
      setError(handleApiError(err));
      setMovies([]);
      setSuggestions([]);
      setShowDropdown(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMovieClick = (movie: Movie) => {
    setQuery(movie.omdb_title || movie.cleaned_title);
    setShowDropdown(false);
    if (onMovieSelect) {
      onMovieSelect(movie);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion);
    setShowDropdown(false);
    if (onSearch) {
      onSearch(suggestion, searchType);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setShowDropdown(false);
    } else if (event.key === 'Enter' && query.trim()) {
      setShowDropdown(false);
      if (searchType === 'movie' && movies.length > 0) {
        handleMovieClick(movies[0]);
      } else if (onSearch) {
        onSearch(query, searchType);
      }
    }
  };


  return (
    <div ref={searchRef} className={`relative ${className}`}>
      <div className="flex gap-2">
        {/* Search Type Selector */}
        <select
          value={searchType}
          onChange={(e) => {
            setSearchType(e.target.value as SearchType);
            setQuery('');
            setMovies([]);
            setSuggestions([]);
            setShowDropdown(false);
          }}
          className="px-3 py-3 text-lg text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm min-w-[120px]"
        >
          <option value="movie">🎬 Movie</option>
          <option value="actor">🎭 Actor</option>
          <option value="director">🎬 Director</option>
          <option value="genre">🎭 Genre</option>
        </select>

        {/* Search Input */}
        <div className="relative flex-1">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => query.length >= 2 && (movies.length > 0 || suggestions.length > 0) && setShowDropdown(true)}
            placeholder={getPlaceholder()}
            className="w-full px-4 py-3 pl-12 text-lg text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm transition-shadow"
          />
          
          {/* Search Icon */}
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          {/* Loading Spinner */}
          {isLoading && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
            </div>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mt-2 text-red-600 text-sm">
          {error}
        </div>
      )}

      {/* Search Results Dropdown */}
      {showDropdown && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-96 overflow-y-auto">
          {/* Movie Results */}
          {searchType === 'movie' && movies.length > 0 && movies.map((movie) => (
            <div
              key={movie.id}
              onClick={() => handleMovieClick(movie)}
              className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">{movie.omdb_title || movie.cleaned_title}</h3>
                  <p className="text-sm text-gray-600">
                    {movie.year && `${movie.year} • `}
                    {movie.director && `Dir: ${movie.director}`}
                  </p>
                  {movie.plot && movie.plot !== "NO MATCH FOUND" && (
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                      {movie.plot.length > 100 ? `${movie.plot.substring(0, 100)}...` : movie.plot}
                    </p>
                  )}
                </div>
                <div className="ml-3 text-right flex-shrink-0">
                  {movie.imdb_rating && (
                    <div className="text-sm font-medium text-yellow-600">
                      ⭐ {movie.imdb_rating}
                    </div>
                  )}
                  {movie.rotten_tomatoes && (
                    <div className="text-xs text-red-600 mt-1">
                      🍅 {movie.rotten_tomatoes}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* Suggestions for Actor/Director/Genre */}
          {searchType !== 'movie' && suggestions.length > 0 && suggestions.map((suggestion, index) => (
            <div
              key={index}
              onClick={() => handleSuggestionClick(suggestion)}
              className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="text-gray-400">
                  {searchType === 'actor' && '🎭'}
                  {searchType === 'director' && '🎬'}
                  {searchType === 'genre' && '🎭'}
                </div>
                <span className="font-medium text-gray-900">{suggestion}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No Results Message */}
      {showDropdown && movies.length === 0 && suggestions.length === 0 && query.length >= 2 && !isLoading && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-4 text-center text-gray-500">
          No {searchType}s found for "{query}"
        </div>
      )}
    </div>
  );
};

export default MovieSearch;